import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import goalsApi from "../api/goalsApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import {
  GOAL_PRIORITIES,
  GOAL_STATUSES,
  GOAL_TYPES,
  PERFORMANCE_QUERY_KEYS,
} from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useGoalCycleOptions from "../hooks/useGoalCycleOptions";

const GoalsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const {
    goalCycleOptions,
    isLoading: goalCyclesLoading,
    isError: goalCyclesError,
    error: goalCyclesErrorObj,
    refetch: refetchGoalCycles,
  } = useGoalCycleOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const [status, setStatus] = React.useState("");

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadGoals = can(PERFORMANCE_PERMISSIONS.GOALS_READ);
  const canCreateGoals = can(PERFORMANCE_PERMISSIONS.GOALS_CREATE);

  if (!canReadGoals) {
    return (
      <ModulePageShell
        title="Goals"
        description="Manage my and team goals with filters, details, and lifecycle actions."
      >
        <EmptyState message="You do not have permission to view goals." />
      </ModulePageShell>
    );
  }

  const goalsQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.goals, status],
    queryFn: () =>
      goalsApi.listGoals({ status: status || undefined, page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: goalsApi.createGoal,
    onSuccess: () => {
      toast({ title: "Goal created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.goals],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create goal",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      employeeId: "",
      managerId: "",
      goalCycleId: "",
      title: "",
      description: "",
      goalType: "OKR",
      weightage: 0,
      targetValue: 0,
      achievedValue: 0,
      unit: "",
      startDate: "",
      dueDate: "",
      status: "DRAFT",
      priority: "MEDIUM",
    },
  });

  const onSubmit = (values) => {
    createMutation.mutate(values);
  };

  const rows = goalsQuery.data?.data || [];
  const hasGoalCycleOptions = goalCycleOptions.length > 0;

  return (
    <ModulePageShell
      title="Goals"
      description="Manage my and team goals with filters, details, and lifecycle actions."
      onRefresh={() => goalsQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateGoals ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={3} align="stretch">
              <Text fontWeight="semibold">Create Goal</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Employee ID</FormLabel>
                  <Select
                    placeholder="Select employee"
                    {...register("employeeId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Manager ID</FormLabel>
                  <Select
                    placeholder="Select manager"
                    {...register("managerId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Goal Cycle</FormLabel>
                  <Select
                    placeholder="Select goal cycle"
                    {...register("goalCycleId")}
                    isDisabled={goalCyclesLoading}
                  >
                    {goalCycleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {!goalCyclesLoading && !hasGoalCycleOptions && (
                    <HStack mt={1} justify="space-between" align="center">
                      <Text fontSize="xs" color="orange.500">
                        No goal cycle found.
                      </Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        onClick={() =>
                          navigate("/performance/masters/goal-cycles")
                        }
                      >
                        Open Goal Cycles Master
                      </Button>
                    </HStack>
                  )}
                  {goalCyclesError ? (
                    <HStack mt={1} justify="space-between" align="center">
                      <Text fontSize="xs" color="red.500">
                        {goalCyclesErrorObj?.response?.data?.message ||
                          "Failed to load goal cycles."}
                      </Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="red"
                        onClick={() => refetchGoalCycles()}
                      >
                        Retry
                      </Button>
                    </HStack>
                  ) : null}
                </FormControl>
              </HStack>

              <HStack>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input {...register("title")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select {...register("goalType")}>
                    {GOAL_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Priority</FormLabel>
                  <Select {...register("priority")}>
                    {GOAL_PRIORITIES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <HStack>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input type="date" {...register("startDate")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Due Date</FormLabel>
                  <Input type="date" {...register("dueDate")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select {...register("status")}>
                    {GOAL_STATUSES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <HStack justify="space-between">
                <HStack>
                  <Text fontSize="sm" color="gray.500">
                    Goal check-ins are available from goal detail pages.
                  </Text>
                </HStack>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isDisabled={!goalCyclesLoading && !hasGoalCycleOptions}
                  isLoading={isSubmitting || createMutation.isPending}
                >
                  Create Goal
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to goals, but cannot create goals." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="semibold">Goal Grid</Text>
          <Select
            maxW="220px"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Filter by status"
          >
            {GOAL_STATUSES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </HStack>

        {goalsQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No goals found for the selected filter." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Goal</Th>
                <Th>Type</Th>
                <Th>Weight</Th>
                <Th>Progress</Th>
                <Th>Status</Th>
                <Th>Due Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((goal) => (
                <Tr key={goal._id}>
                  <Td>{goal.title}</Td>
                  <Td>{goal.goalType}</Td>
                  <Td>{goal.weightage ?? 0}</Td>
                  <Td>{goal.progressPercentage ?? 0}%</Td>
                  <Td>{goal.status}</Td>
                  <Td>
                    {goal.dueDate
                      ? new Date(goal.dueDate).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => navigate(`/performance/goals/${goal._id}`)}
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default GoalsPage;
